import {
    AlgoliaFilterMobileFiltersButton,
    AlgoliaRouting,
    CustomCurrentRefinements,
    CustomRefinementList,
} from "@components/AlgoliaCustomComponents";
import Autocomplete from "@components/AlgoliaCustomComponents/Autocomplete/Autocomplete";
import { QueryStringStorageProvider } from "@contexts/QueryStringStorageContext";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjArrowLeft } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiArrowLeft } from "@mdi/js";
import algoliasearch, { SearchClient } from "algoliasearch/lite";
import classnames from "classnames";
import connectVoiceSearch from "instantsearch.js/es/connectors/voice-search/connectVoiceSearch";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Configure, InstantSearch, useConnector } from "react-instantsearch";

import { CONTENT_TYPES, INDEX_CONTENT } from "../../search/algolia-indexes";
import CustomRecipeHits from "./CustomRecipeHits/CustomRecipeHits";
import style from "./style.module.scss";
import getTranslation from "./util/getTranslation";

interface RecipeGridProps {
    specialty?: {
        id: string;
        title: string;
    }[];
    enabledFilters: {
        diseases: boolean;
        courses: boolean;
        dietaryWishes: boolean;
        levelsOfComplexity: boolean;
    };
    placement?: string;
}

interface UseVoiceSearchProps {
    isBrowserSupported?: boolean;
    isListening?: boolean;
    toggleListening?: () => void;
}

// Algolia provides a pagination API.
// We don't use it with the load more feature, so setting this to max value.
const HITS_PER_PAGE = 1000;

export const useVoiceSearch = (props?: UseVoiceSearchProps): UseVoiceSearchProps => {
    return useConnector<VoiceSearchConnectorParams, VoiceSearchWidgetDescription>(connectVoiceSearch, props);
};

export const RecipeGrid = (props: RecipeGridProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    const { enabledFilters, placement = null, specialty } = props;
    const { t } = useTranslation();
    const locale = useLocale();
    const placementHashString = placement.replaceAll(/ |:|\|/g, "");

    let specialtyHashString = "";

    if (specialty) {
        specialty.forEach((spec) => {
            specialtyHashString = `${specialtyHashString}${spec.id}`;
        });
    }

    const hashKey = `${placementHashString}-${specialtyHashString}`;

    const algoliaClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY);

    const [showFiltersModal, setShowFiltersModal] = useState(false);

    const renderRecipeRefinements = (): JSX.Element => {
        const filters = [];
        if (enabledFilters.diseases) filters.push("specialty");
        if (enabledFilters.courses) filters.push("course");
        if (enabledFilters.dietaryWishes) filters.push("dietary_wishes");
        if (enabledFilters.levelsOfComplexity) filters.push("difficulty");

        return (
            <div className={style.recipeRefinementFilters}>
                {filters.map((item: string, index: number) => {
                    return (
                        <CustomRefinementList
                            attribute={item}
                            key={`filter-${index}`}
                            sortBy={["name:asc"]}
                            relativePosition={isMobile}
                            showCount={isMobile}
                            getTranslation={getTranslation}
                        />
                    );
                })}
            </div>
        );
    };

    const specialtyFiltersArray =
        specialty?.length &&
        specialty.map((spec) => {
            return `specialty:"${spec.title}"`;
        });

    const specialtyFiltersString = specialtyFiltersArray?.length && specialtyFiltersArray.join(" OR ");
    const isMobile = useMediaQuery(BreakPoint.md);
    const filtersString = specialtyFiltersString
        ? `locale:${locale} AND (${specialtyFiltersString}) AND content_type:'${CONTENT_TYPES.RECIPE}'`
        : `locale:${locale} AND content_type:'${CONTENT_TYPES.RECIPE}'`;

    // add filters param if not exists on initial load to Algolia.
    const algoliaSearchClient = {
        ...algoliaClient,
        search(requests) {
            const params = requests?.[0]?.params;
            if (!params?.filters) {
                params.filters = filtersString;
            }
            return algoliaClient.search(requests);
        },
    } as SearchClient;

    return (
        <QueryStringStorageProvider hashKey={hashKey}>
            <InstantSearch indexName={INDEX_CONTENT} searchClient={algoliaSearchClient} routing={AlgoliaRouting}>
                <div>
                    <div className={style.searchBox}>
                        <Autocomplete
                            searchClient={algoliaClient}
                            placeholder={t("Search for recipes", { ns: "recipe" })}
                            indexName={INDEX_CONTENT}
                            contentType={CONTENT_TYPES.RECIPE}
                        />
                    </div>
                    <Configure filters={filtersString} hitsPerPage={HITS_PER_PAGE} />
                    {isMobile && <AlgoliaFilterMobileFiltersButton setShowFiltersModal={setShowFiltersModal} />}
                    <div className={classnames(style.recipeGridControls, showFiltersModal && style.open)}>
                        <div className={style.header}>
                            <button
                                className={style.back}
                                onClick={() => setShowFiltersModal(false)}
                                title={t("Back", { ns: "common" })}
                            >
                                <Icon
                                    color="black"
                                    size={jnjFullBranded ? undefined : "large"}
                                    icon={jnjFullBranded ? jnjArrowLeft : mdiArrowLeft}
                                    type={jnjFullBranded ? "jnj" : "mdi"}
                                    {...(jnjFullBranded ? { verticalAlign: "middle" } : {})}
                                />
                            </button>
                            <Typography
                                className={style.title}
                                color={jnjFullBranded ? "dark" : "primary"}
                                size={jnjFullBranded ? "xl" : "3xl"}
                                weight={jnjFullBranded ? "400" : "500"}
                                font="title"
                            >
                                {t("Filters", { ns: "common" })}
                            </Typography>
                        </div>

                        {renderRecipeRefinements()}

                        <div className={style.modalShowResultsButton}>
                            <Button color="primary" onClick={() => setShowFiltersModal(false)}>
                                <Typography
                                    size={jnjFullBranded ? "inherit" : "m"}
                                    font="text"
                                    weight={jnjFullBranded ? undefined : "700"}
                                    color="white"
                                >
                                    {t("Show results", { ns: "common" })}
                                </Typography>
                            </Button>
                        </div>

                        <div className={style.recipeRefinementTags}>
                            <CustomCurrentRefinements getTranslation={getTranslation} />
                        </div>
                    </div>
                    {<CustomRecipeHits placement={placement} />}
                </div>
            </InstantSearch>
        </QueryStringStorageProvider>
    );
};

export default RecipeGrid;
